// Componente Home público
// Página de bienvenida accesible sin autenticación

import React from 'react';
import { Link } from 'react-router-dom';
import { FiHeart, FiUsers, FiShield, FiArrowRight, FiUser, FiRadio, FiMapPin, FiMessageCircle } from 'react-icons/fi';
import Button from '../components/UI/Button';
import AmarsiLogo from '../components/Icons/AmarsiLogo';
import ThemeToggleButton from '../components/UI/ThemeToggleButton';

/**
 * Componente Home
 * 
 * Página de bienvenida pública que muestra información sobre Amarsi
 * y permite el acceso al login para usuarios registrados
 */

// Configuración de imágenes de fondo por sección
const backgroundImages = {
  hero: {
    mobile: 'url("https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80&fp=800")',
    desktop: 'url("https://images.unsplash.com/photo-1529156069898-49953e39b3ac?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")',
  },
  features: {
    mobile: 'url("https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80&fp=800")',
    desktop: 'url("https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")',
  },
  pasos: {
    mobile: 'url("https://images.unsplash.com/photo-1529156069898-49953e39b3ac?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80&fp=800")',
    desktop: 'url("https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80")',
  },
};

const Home = () => {
  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header + Hero Section con parallax */}
      <header className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* Imagen de fondo con parallax */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed bg-no-repeat"
          style={{
            backgroundImage: backgroundImages.hero.desktop,
          }}
        >
          {/* Imagen alternativa para móvil - amigos riendo */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat sm:hidden"
            style={{
              backgroundImage: backgroundImages.hero.mobile,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        </div>
        
        {/* Contenido */}
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Logo y branding */}
            <div className="flex justify-center items-center mb-12">
              <div className="flex items-center justify-between gap-4 sm:gap-6 w-full max-w-[60%]">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-black text-white">
                    Amarsi
                  </h1>
                  <p className="text-xs sm:text-sm text-white/80 font-medium tracking-wider uppercase">
                    Mejores anecdotas.
                  </p>
                </div>
                <AmarsiLogo width="48" height="48" className="sm:w-16 sm:h-16" />
              </div>
            </div>
            
            {/* Hero content */}
            <div className="text-center">          
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 sm:mb-8 leading-tight">
                Conecta con personas
                <span className="block text-white/90 mt-2 sm:mt-3">cerca de ti</span>
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
                <span className="font-semibold text-white">Amarsi</span> te ayuda a encontrar conexiones auténticas.
                Descubre nuestra función Radar y expande tu círculo social.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
                <Link to="/auth/login" className="w-full sm:w-auto">
                  <Button variant="primary" size="lg" className="w-full sm:w-auto bg-primary-500 text-white hover:bg-primary-600">
                    Iniciar Sesión
                    <FiArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/auth/register" className="w-full sm:w-auto">
                  <Button variant="ghost-primary" size="lg" className="w-full sm:w-auto bg-white/10 !text-white border-white hover:bg-white/20">
                    Regístrate gratis
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        {/* Indicador de scroll */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </div>
      </header>

      {/* Pasos Interactivos Section con parallax */}
      <PasosInteractivos />

      {/* Features Section con parallax */}
      <Features/>

      {/* Footer */}
      <footer className="bg-neutral-800 border-t border-gray-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col items-center space-y-4 text-center sm:text-center md:flex-row md:justify-between md:items-start md:text-left md:space-y-0">
            <div className="flex flex-col items-center space-y-3 md:items-start md:space-y-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <AmarsiLogo width='2.5rem' height='2.5rem' className="w-10 h-10"/>
                <div>
                  <span className="text-gray-900 dark:text-white font-bold text-base sm:text-lg">Amarsi</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium tracking-wider uppercase">
                    Mejores anecdotas.
                  </p>
                </div>
              </div>
              <span className="text-gray-500 text-xs sm:text-sm">© {new Date().getFullYear()} Amarsi. Todos los derechos reservados.</span>
            </div>
            <div className="flex flex-col items-center space-y-2 sm:flex-row sm:space-y-0 sm:space-x-6 sm:items-start text-sm">
              <Link 
                to="/premium" 
                className="text-primary dark:text-primary font-medium hover:opacity-80 transition-opacity text-xs sm:text-sm"
              >
                Premium
              </Link>
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors text-xs sm:text-sm"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors text-xs sm:text-sm"
              >
                Privacidad
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

function PasosInteractivos() {
  return (
    <div className="relative min-h-screen flex items-center overflow-hidden">
      {/* Imagen de fondo con parallax */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed bg-no-repeat"
        style={{
          backgroundImage: backgroundImages.pasos.desktop,
        }}
      >
        {/* Imagen alternativa para móvil - grupo de amigos */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat sm:hidden"
          style={{
            backgroundImage: backgroundImages.pasos.mobile,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black-800/70 to-black/90" />
      </div>
      
      {/* Contenido */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="relative max-w-7xl mx-auto">
        {/* Encabezado de la sección */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="flex justify-center mb-6">
            <AmarsiLogo width="40" height="40" className="sm:w-12 sm:h-12" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-4">
            Tu próxima conexión en <span className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-400 dark:to-primary-500 bg-clip-text text-transparent">Amarsi</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Comienza tu viaje en <span className="font-semibold text-primary-600 dark:text-primary-400">Amarsi</span> y descubre personas cerca de ti
          </p>
        </div>

        {/* Pasos en grid responsive */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
          {/* Paso 1 */}
          <div className="relative group">
            <div className="relative bg-white/30 dark:bg-black/30 backdrop-blur-md p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/20 dark:border-white/10">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/50 dark:bg-white/10 rounded-xl flex items-center justify-center text-primary-600 dark:text-primary-400 font-black text-xl sm:text-2xl mb-6">
                1
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Crea tu perfil
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Regístrate y completa tu perfil básico en segundos
              </p>
              <div className="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold">
                <span>30 segundos</span>
              </div>
            </div>
          </div>

          {/* Paso 2 */}
          <div className="relative group">
            <div className="relative bg-white/30 dark:bg-black/30 backdrop-blur-md p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/20 dark:border-white/10">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/50 dark:bg-white/10 rounded-xl flex items-center justify-center text-primary-600 dark:text-primary-400 font-black text-xl sm:text-2xl mb-6">
                2
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Explora el Radar
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Activa el Radar y descubre personas cercanas compatibles
              </p>
              <div className="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold">
                <span>Tiempo real</span>
              </div>
            </div>
          </div>

          {/* Paso 3 */}
          <div className="relative group">
            <div className="relative bg-white/30 dark:bg-black/30 backdrop-blur-md p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-white/20 dark:border-white/10">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/50 dark:bg-white/10 rounded-xl flex items-center justify-center text-primary-600 dark:text-primary-400 font-black text-xl sm:text-2xl mb-6">
                3
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Conecta y chatea
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                Envía mensajes y crea conexiones auténticas
              </p>
              <div className="inline-flex items-center text-primary-600 dark:text-primary-400 font-semibold">
                <span>Instantáneo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Botón CTA de la sección */}
        <div className="text-center mt-12 sm:mt-16">
          <Link to="/auth/register" className="group inline-flex items-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300">
            <span>Comienza ahora</span>
            <FiArrowRight className="ml-3 h-5 w-5 group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  </div>
  );
}

function Features() {
  return (
    <div className="relative min-h-screen flex items-center overflow-hidden">
      {/* Imagen de fondo con parallax */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-fixed bg-no-repeat"
        style={{
          backgroundImage: backgroundImages.features.desktop,
        }}
      >
        {/* Imagen alternativa para móvil - personas conversando */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat sm:hidden"
          style={{
            backgroundImage: backgroundImages.features.mobile,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/75 to-white/85 dark:from-neutral-900/85 dark:via-neutral-800/75 dark:to-neutral-900/85" />
      </div>
      
      {/* Contenido */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="relative max-w-7xl mx-auto">
        {/* Encabezado de la sección */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <div className="flex justify-center mb-6">
            <AmarsiLogo width="40" height="40" className="sm:w-12 sm:h-12" />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-400 dark:to-primary-500 bg-clip-text text-transparent mb-4">
            Características de Amarsi
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Experiencias diseñadas para crear conexiones auténticas
          </p>
        </div>

        {/* Grid de features con diseño moderno */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Feature 1: Radar Social */}
          <div className="group relative">
            <div className="relative h-full p-6 sm:p-8 bg-white/30 dark:bg-neutral-800/30 backdrop-blur-md rounded-2xl border border-white/20 dark:border-white/10 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/50 dark:bg-white/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FiMapPin className="h-7 w-7 sm:h-8 sm:w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Radar Social
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Descubre personas cercanas en tiempo real con nuestra tecnología de geolocalización avanzada.
              </p>
            </div>
          </div>

          {/* Feature 2: Chat Instantáneo */}
          <div className="group relative">
            <div className="relative h-full p-6 sm:p-8 bg-white/30 dark:bg-neutral-800/30 backdrop-blur-md rounded-2xl border border-white/20 dark:border-white/10 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/50 dark:bg-white/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FiMessageCircle className="h-7 w-7 sm:h-8 sm:w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Chat Instantáneo
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Comunícate sin límites con mensajes en tiempo real y comparte tus intereses.
              </p>
            </div>
          </div>

          {/* Feature 3: Seguridad EASI */}
          <div className="group relative">
            <div className="relative h-full p-6 sm:p-8 bg-white/30 dark:bg-neutral-800/30 backdrop-blur-md rounded-2xl border border-white/20 dark:border-white/10 hover:shadow-xl transition-all duration-300">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/50 dark:bg-white/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <FiShield className="h-7 w-7 sm:h-8 sm:w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                Seguridad EASI
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Tu protección es prioridad con verificación de identidad y políticas de seguridad estrictas.
              </p>
            </div>
          </div>
        </div>

        {/* Estadísticas */}
        <div className="mt-16 sm:mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 text-center">
          <div className="group">
            <div className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">
              50K+
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Usuarios Activos</p>
          </div>
          <div className="group">
            <div className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-primary-600 to-primary-500 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">
              1M+
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Conexiones</p>
          </div>
          <div className="group">
            <div className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-primary-500 to-primary-600 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">
              4.8★
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Calificación</p>
          </div>
          <div className="group">
            <div className="text-3xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform">
              24/7
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Soporte</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
}

export default Home;
